import { toast } from "react-toastify";
import getErrorMessage from "../utils/getErrorMessage";

const doRequest = async (path: string, requestOptions: any = {}, showErrorToast = true) => {
  const options = Object.assign(requestOptions, {
    credentials: "include"
  });
  try {
    const response = await fetch(`${process.env.REACT_APP_API_HOST}${path}`, options);

    if (showErrorToast && !response.ok) {
      toast.error(await getErrorMessage(response), { toastId: response.status === 401 ? 'auth_error' : path });
    }
    return response;
  } catch (error) {
    if (showErrorToast) {
      toast.error("Could not connect to the Fixie API. Please check your internet connection and try again.");
    }
    throw new Error('Could not connect to the Fixie API');
  }
}

const doPost = async (path: string, data: any, showErrorToast = true) => doRequest(path, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(data)
}, showErrorToast);

const doPut = async (path: string, data?: any, showErrorToast = true) => doRequest(path, {
  method: "PUT",
  headers: { "Content-Type": "application/json" },
  body: data ? JSON.stringify(data) : undefined
}, showErrorToast);

export const doSignup = async (data: any) => doPost(`/account`, data, false);

export const doVercelSignup = async (data: any) => doPost(`/vercel`, data, false);

export const doPasswordLogin = async (username: string, password: string) => doPost(`/login/password`, { username, password }, false);
export const linkVercelAccountViaPasswordLogin = async (username: string, password: string, vercelCode: string, vercelConfigurationId: string) =>
  doPost(`/login-vercel/password`, { username, password, vercelCode, vercelConfigurationId }, false);

export const logOut = async () => doPut('/logout');

export const listProxyApplications = async (showToast: boolean, stripeCheckoutSessionId?: string) => stripeCheckoutSessionId ?
  doRequest(`/account/proxy-application?stripeCheckoutSessionId=${stripeCheckoutSessionId}`, {}, showToast) :
  doRequest('/account/proxy-application', {}, showToast);

export const getProxyApplication = async (id: string) => doRequest(`/account/proxy-application/${id}`);

export const createProxyApplication = async (data: any) => doPost(
  '/account/proxy-application',
  data,
  false // Because expected behavior is to get a 402 is payment is required, skip automatic error handling
);

export const linkVercelAccount = async (vercelCode: string | null, vercelConfigurationId: string | null) =>
  doPost(`/link-vercel`, { vercelCode, vercelConfigurationId }, false);

export const updateProxyApplicationPlan = async (id: string, newPlanName: string) => doPut(`/account/proxy-application/${id}`, { newPlanName });

export const deleteProxyApplication = async (id: string) => doRequest(`/account/proxy-application/${id}`, {
  method: "DELETE"
});

export const getProxyApplicationLogs = async (id: string, rpp: number, exclusiveStartKey: string) => doRequest(`/account/proxy-application/${id}/logs?exclusiveStartKey=${exclusiveStartKey}&perPage=${rpp}`);

export const createDashboardUser = async (data: any) => doPost('/account/dashboard-user', data);

export const listDashboardUsers = async (showToast: boolean) => doRequest('/account/dashboard-user', {}, showToast);
export const deleteDashboardUser = async (id: string) => doRequest(`/account/dashboard-user/${id}`, {
  method: "DELETE"
});

export const bootstrap = async () => doRequest(`/bootstrap`, {}, false);

export const getVercelProjects = async (search?: string) =>
  search ? doRequest(`/vercel/projects?search=${search}`, {}, false) : doRequest(`/vercel/projects`);

export const updateProfile = async (data: any) => doPut(`/profile`, data);

export const getDiscount = async () => doRequest(`/account/stripe/discount`);
export const addCouponCode = async (data: any) => doPost(`/account/stripe/discount`, { data });

export const initiatePasswordReset = async (email: string) => doPost('/password-reset/recover', { email });

export const resetPassword = async (password: string, token: string) => doPost('/password-reset/reset', { password, token });
